import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { useSettings } from '../../context/SettingsContext';
import { useViewportDevice, useVisibleHomeCategories } from '../../lib/homeCategories';
import { cacheSliders, readCachedSliders } from '../../lib/sliderCache';
import { slidersAt } from '../../lib/sliderPlacement';
import useSwipe from '../../hooks/useSwipe';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import AuthModal from '../../components/AuthModal';
import ProviderStrip, { providersFromResponse } from '../../components/ProviderStrip';
import BetrunSection from './BetrunSection';
import BetrunGameCard from './BetrunGameCard';
import BetrunMatchCard from './BetrunMatchCard';
import { TOP_MATCHES_ENDPOINT, topMatchesAvailable } from '../../lib/topMatches';
import { sportsbookHref } from '../../lib/sportsbookProviders';
import {
  SECTIONS, HOOKS, bonusHook, bonusIdHook, hook, sectionHook,
} from '../../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../../components/HomeBlocks';
import { featuredItems } from '../../lib/showcaseStrip';
import GuestWelcomeSlot from '../../components/GuestWelcomeSlot';
import BonusHtmlThumb, { bonusHasHtmlArt } from '../../components/BonusHtmlThumb';

const STRIP_LIMIT = 14;
// Panelde hiç oyun işaretlenmemişse otomatik kaynaktan gelen kart sayısı.
const FEATURE_LIMIT = 7;
// Panel seçimi bu sayıya kadar olduğu gibi çizilir (uç tavanı 50). Eskiden
// istek FEATURE_LIMIT ile yapılıyordu ve işaretlenen oyunların yalnızca ilk
// yedisi şeride giriyordu (Betrun, 26.09.2026).
const SHOWCASE_MAX = 50;
// Hero'da bannerlar tam genişlikte, tek tek sayfalanıp döner.
const HERO_PER_PAGE = 1;
const HERO_INTERVAL = 6000;

const mapGame = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

const BetrunHome = () => {
  const settings = useSettings();
  const gl = useGameLauncher();

  // Klasik vitrinle aynı önbellek: yanıt beklenirken son bannerlar çizilir,
  // taze liste gelince üzerine yazılır (bkz. lib/sliderCache.js).
  const [banners, setBanners] = useState(readCachedSliders);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [live, setLive] = useState([]);
  const [matches, setMatches] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [heroPage, setHeroPage] = useState(0);
  const visibleCategories = useVisibleHomeCategories(settings.sports_enabled);
  // Kategori alanı cihaza göre iki ayrı settir (panelde kartın "Görünür
  // olduğu cihaz" alanı): masaüstünde dikey kartlar, telefonda ikon kutuları.
  // Kap hangi setin çizildiğini `data-bhs-device` ile söyler; panelden
  // yazılan CSS iki seti ayrı seçebilsin diye.
  const categoryDevice = useViewportDevice();

  useEffect(() => {
    const get = (url, params) => client.get(url, { params }).then(({ data }) => data).catch(() => null);

    // Ağ hatasında (`data === null`) önbellekten çizilen bannerlar korunur.
    get('/sliders').then((data) => {
      if (!data) return;
      cacheSliders(data.items || []);
      setBanners(data.items || []);
    });
    // Vitrin şeritleri panelden seçilir (Oyun Yönetimi → Ana Sayfa Vitrini);
    // hiç oyun işaretlenmemişse uç otomatik kaynakla doldurur, şerit boş kalmaz.
    get('/games/showcase', { strip: 'featured', category: 'slot', limit: SHOWCASE_MAX })
      .then((data) => setFeatured(featuredItems(data, FEATURE_LIMIT)));
    // Üstteki şeritle örtüşenler ayıklanacağı için fazla istenir: panel
    // seçimi elliye kadar çıkabilir.
    get('/games/showcase', { strip: 'popular', category: 'slot', limit: SHOWCASE_MAX })
      .then((data) => setPopular(data?.items || []));
    get('/games', { category: 'live-casino', limit: STRIP_LIMIT }).then((data) => setLive(data?.items || []));
    get('/game-providers', { category: 'slot' }).then((data) => {
      setProviders(providersFromResponse(data));
    });
    get('/bonuses').then((data) => setBonuses((data?.items || data || []).slice(0, 5)));
  }, []);

  // Öne çıkan maçlar yalnızca Digitain yayındayken istenir; aksi hâlde uç
  // zaten boş döner ama her ana sayfa açılışında boşuna bir istek olurdu.
  const matchesOn = topMatchesAvailable(settings);
  useEffect(() => {
    if (!matchesOn) {
      setMatches([]);
      return undefined;
    }
    let active = true;
    client.get(TOP_MATCHES_ENDPOINT)
      .then(({ data }) => { if (active) setMatches(data?.items || []); })
      .catch(() => { if (active) setMatches([]); });
    return () => { active = false; };
  }, [matchesOn]);

  // Betrun hero'su yalnızca görseli çizer (metin/CTA alanı yok); görselsiz
  // banner burada boş bir kutu olurdu, bu yüzden elenir.
  // Yalnızca ana slider banner'ları: 'side' ve katalog sayfası banner'ları
  // buraya düşmemelidir (bkz. lib/sliderPlacement.js).
  const visibleBanners = slidersAt(banners).filter(
    (banner) => (
      banner.image
      && (banner.cta_link !== '/spor' || settings.sports_enabled !== false)
    ),
  );
  // Bannerlar tek tek sayfalanır; paneldeki tüm görünür bannerlar sırayla döner.
  const heroPageCount = Math.max(1, Math.ceil(visibleBanners.length / HERO_PER_PAGE));
  const activeHeroPage = heroPage % heroPageCount;
  const heroBanners = visibleBanners.slice(
    activeHeroPage * HERO_PER_PAGE,
    activeHeroPage * HERO_PER_PAGE + HERO_PER_PAGE,
  );

  // Sayaç heroPage'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı
  // tam süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (heroPageCount < 2) return undefined;
    const timer = setInterval(
      () => setHeroPage((page) => (page + 1) % heroPageCount),
      HERO_INTERVAL,
    );
    return () => clearInterval(timer);
  }, [heroPageCount, heroPage]);

  // Mobilde banner parmakla çevrilir; Betrun'da ok yoktur, tek kumanda noktalar
  // ve bu jesttir.
  const heroSwipe = useSwipe({
    enabled: heroPageCount > 1,
    onNext: () => setHeroPage((page) => (page + 1) % heroPageCount),
    onPrev: () => setHeroPage((page) => (page - 1 + heroPageCount) % heroPageCount),
  });

  // İki şerit de otomatik dolduğunda aynı oyunları göstermesin.
  const featuredIds = new Set(featured.map((game) => game.id));
  const popularStrip = popular
    .filter((game) => !featuredIds.has(game.id))
    .slice(0, STRIP_LIMIT);

  return (
    <>
      {/* Giriş yapmamış ziyaretçiye özel kodla doldurulan karşılama alanı;
          bölüm sıralayıcısının dışında, her zaman en üsttedir. */}
      <GuestWelcomeSlot />
      <HomeBlocks theme="betrun">
        <HomeBlock id={SECTIONS.HERO}>
        {heroBanners.length > 0 && (
          <div className="betrun-hero-wrap" {...heroSwipe} {...sectionHook(SECTIONS.HERO)}>
            <div
              key={activeHeroPage}
              className={`betrun-hero ${heroBanners.length === 1 ? 'is-single' : ''}`}
                {...hook(HOOKS.SLIDER)}
            >
              {heroBanners.map((banner, index) => (
                <Link
                  key={banner.id || index}
                  to={banner.cta_link || '/promosyonlar'}
                  className="betrun-hero-item"
                >
                  <img src={resolveApiAssetUrl(banner.image)} alt={banner.title || 'Kampanya'} />
                </Link>
              ))}
            </div>
            {heroPageCount > 1 && (
              <div className="betrun-hero-dots">
                {Array.from({ length: heroPageCount }, (_, page) => (
                  <button
                    key={page}
                    type="button"
                    className={`betrun-hero-dot ${page === activeHeroPage ? 'is-active' : ''}`}
                    onClick={() => setHeroPage(page)}
                    aria-label={`${page + 1}. banner`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.CATEGORIES}>
        {/* Kategori kutuları: ortada panelden yüklenen görsel (Betrun'da
            şeffaf zeminli 3D ikon), altında tek satır altın başlık. Eski
            banner kartındaki alt başlık başlığın devamı sayılır ("SLOT" +
            "OYUNLARI"), CTA metni çizilmez. Bağlantı dış adres de olabilir
            (Telegram) — o zaman yönlendirici değil düz bağlantı kullanılır. */}
        {visibleCategories.length > 0 && (
          <div
            className={`betrun-tiles is-${categoryDevice}`}
            data-bhs-device={categoryDevice}
            {...sectionHook(SECTIONS.CATEGORIES)}
          >
            {visibleCategories.map((category) => {
              const label = [category.title, category.subtitle].filter(Boolean).join(' ');
              const href = category.href || '/';
              const external = /^https?:\/\//i.test(href);
              const body = (
                <>
                  <span className="betrun-tile-art-wrap">
                    {category.image && (
                      <img
                        className="betrun-tile-art"
                        src={resolveApiAssetUrl(category.image)}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    )}
                  </span>
                  <span className="betrun-tile-title">{label}</span>
                </>
              );
              return external ? (
                <a
                  key={category.id}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="betrun-tile"
                  {...hook(HOOKS.CATEGORY_CARD)}
                >
                  {body}
                </a>
              ) : (
                <Link
                  key={category.id}
                  to={href}
                  className="betrun-tile"
                  {...hook(HOOKS.CATEGORY_CARD)}
                >
                  {body}
                </Link>
              );
            })}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PICKS}>
        {featured.length > 0 && (
          <BetrunSection section={SECTIONS.PICKS} title="Size Özel Seçtiklerimiz">
            {featured.map((game) => (
              <BetrunGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </BetrunSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.POPULAR}>
        {popularStrip.length > 0 && (
          <BetrunSection section={SECTIONS.POPULAR} title="Popüler Slotlar" to="/slotlar">
            {popularStrip.map((game) => (
              <BetrunGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </BetrunSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_CASINO}>
        {live.length > 0 && (
          <BetrunSection section={SECTIONS.LIVE_CASINO} title="Canlı Casino" to="/canli-casino">
            {live.map((game) => (
              <BetrunGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </BetrunSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.MATCHES}>
        {/* Merit'teki "Yeni Eklenenler" ve "Hızlı Oyunlar" şeritlerinin yerinde
            Digitain'in öne çıkan maçları durur. Hızlı Oyunlar üst kategori
            barından açılır. */}
        {matches.length > 0 && (
          <BetrunSection section={SECTIONS.MATCHES} title="Öne Çıkan Maçlar" to={sportsbookHref('sportdigi')} wide stripClass="is-matches">
            {matches.map((match, index) => (
              <BetrunMatchCard key={match.id || `${match.home}-${match.away}-${index}`} match={match} index={index} />
            ))}
          </BetrunSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PROVIDERS}>
        <ProviderStrip providers={providers} classPrefix="betrun" />
        </HomeBlock>

        <HomeBlock id={SECTIONS.BONUSES}>
        {bonuses.length > 0 && (
          <BetrunSection section={SECTIONS.BONUSES} title="Bonuslar" to="/promosyonlar" wide>
            {bonuses.map((bonus, index) => (
              <Link
                key={bonus.id || index}
                to="/promosyonlar"
                className="betrun-hero-item"
                style={{ paddingTop: '56%' }}
                {...hook(HOOKS.BONUS_CARD)}
                {...bonusIdHook(bonus.id)}
              >
                {bonus.image ? (
                  <img
                    src={resolveApiAssetUrl(bonus.image)}
                    alt={bonus.title || 'Bonus'}
                    {...bonusHook('image')}
                  />
                ) : (
                  <BonusHtmlThumb
                    bonusId={bonus.id}
                    title={bonus.title}
                    enabled={bonusHasHtmlArt(bonus)}
                  >
                    <span
                      style={{
                        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 6, padding: 16,
                        textAlign: 'center',
                      }}
                    >
                      <span style={{ fontSize: 22, fontWeight: 900, color: 'var(--pusu-gold)' }}>
                        {bonus.amount_text || bonus.title}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--pusu-muted)' }}>{bonus.subtitle || bonus.description}</span>
                    </span>
                  </BonusHtmlThumb>
                )}
              </Link>
            ))}
          </BetrunSection>
        )}
        </HomeBlock>
      </HomeBlocks>

      <GameChoiceModal
        game={gl.choice}
        user={gl.user}
        balance={gl.balance}
        bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice}
        onReal={gl.startReal}
        onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')}
        onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal
          key={`${gl.activeGame.id}-${gl.mode}`}
          game={gl.activeGame}
          mode={gl.mode}
          onClose={gl.closeGame}
        />
      )}
      <AuthModal
        open={!!gl.authTab}
        onClose={() => gl.setAuthTab(null)}
        defaultTab={gl.authTab || 'login'}
      />
    </>
  );
};

export default BetrunHome;
